<template>
  <input
    :id
    ref="fileInput"
    class="input-hidden"
    type="file"
    :multiple="multiple"
    :accept="acceptTypes"
    @change="onUpload"
    @click="$refs.fileInput.value = null"
  />
  <UpgradeModal v-model:open="upgradeModalOpen">
    <template #upgradeMessage>
      <Alert
        title="The unlimited asset limit feature is not available to your current plan"
        subtitle="Upgrade to any of our plans to unlock unlimited projects"
        class="mb-6"
      />
    </template>
  </UpgradeModal>
</template>

<script>
import { ref } from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { UpgradeModal } from '@components/common/UpgradeModal';
import _ from 'lodash';
import emitter from 'tiny-emitter/instance';
import { usePremiumFeature } from '@/composables/usePremiumFeature.js';
import { Alert } from '@ui/components/alert';
import { convertBytes } from '../../utils.js';
import requests from '../../vuex/requests.js';
import { EVENT } from '../../constants.js';

export default {
  components: {
    Alert,
    UpgradeModal,
  },

  props: {
    id: {
      type: String,
      default: 'file-uploader-input',
    },
    initAssetId: {
      type: String,
      default: null,
    },
    // The asset is to be added to the end of the container
    initAddEnd: {
      type: Boolean,
      default: false,
    },
    // The uploader will replace the asset with id assetId
    initReplace: {
      type: Boolean,
      default: false,
    },
    // Whether to accept custom messages
    acceptMessages: {
      type: Boolean,
      default: true,
    },
  },

  setup() {
    const { isLimited: isUploadLimited, limit: uploadSizeLimit } =
      usePremiumFeature({
        feature: 'upload_asset',
      });
    const upgradeModalOpen = ref(false);

    return {
      isUploadLimited,
      uploadSizeLimit,
      upgradeModalOpen,
    };
  },

  data() {
    return {
      assetId: null,
      addEnd: false,
      replace: false,
    };
  },

  computed: {
    ...mapGetters('user/projects', ['getOpen']),
    ...mapGetters('project/data/assets', [
      'getIsRecursiveContainer',
      'getContainerNode',
      'getIndex',
      'getNode',
    ]),

    /**
     * Return whether the input will accept multiple files or only one
     * @returns {Boolean}
     */
    multiple() {
      return !this.replace;
    },

    acceptTypes() {
      if (this.replace && this.assetId !== null) {
        const { type } = this.getNode(this.assetId);
        if (type === 'image' || type === 'template-image') {
          return 'image/jpeg, image/jpg, image/png, image/gif';
        }
        if (type === 'audio' || type === 'template-audio') {
          return 'audio/mp3, audio/wav, audio/ogg';
        }
      }
      return 'image/jpeg, image/jpg, image/png, image/gif, audio/mp3, audio/wav, audio/ogg';
    },
  },

  mounted() {
    this.assetId = this.initAssetId;
    this.addEnd = this.initAddEnd;
    this.replace = this.initReplace;
    if (this.acceptMessages) {
      emitter.on(EVENT.OPEN_FILE_UPLOADER, this.onOpenFileUploader);
    }
  },

  beforeUnmount() {
    if (this.acceptMessages) {
      emitter.off(EVENT.OPEN_FILE_UPLOADER);
    }
  },

  methods: {
    ...mapActions('project/data/assets', [
      'createAsset',
      'updateAsset',
      'deleteAsset',
    ]),
    ...mapActions('application/notifications', ['createError']),

    /**
     * Event handler for custom OPEN_FILE_UPLOADER event
     */
    onOpenFileUploader(assetId, addEnd, replace) {
      this.assetId = assetId;
      this.addEnd = addEnd;
      this.replace = replace;

      // In order to open the file modal we need to click
      this.$nextTick(() => {
        this.$refs.fileInput.click();
      });
    },

    /**
     * The input file field has changed
     */
    async onUpload(event) {
      const fileList = event.target.files;
      console.log('The filelist to upload is:', fileList);
      // Convert the FileList object to an array
      const files = Array.from(fileList);

      if (!this.isUploadAllowed(files)) return;

      // We will be replacing the given asset id
      let previousProps = null;
      if (this.replace) {
        // Keep the same asset id for replace
        const id = this.assetId;
        // Store the previous properties of this asset
        previousProps = _.cloneDeep(this.getNode(id));
        delete previousProps.id;
        // Show loader until the replacing has finished
        this.updateAsset({
          id,
          props: { file: 'app-icons/spinner', type: 'icon' },
          apply: true,
        });
        this.uploadFiles(files, [id], [previousProps]);
      } else {
        const assetIds = await this.createTemporaryAssets(files);
        this.uploadFiles(files, assetIds, null);
      }
    },

    /**
     * Check the file size limits
     * @param   {array} files   The various files that are to be aupladed
     * @return  {Boolean}       Whether the sizes of all the files are allowed by the owner's plan
     */
    isUploadAllowed(files) {
      if (this.isUploadLimited) {
        const sizeLimit = this.uploadSizeLimit;

        // Find the first file that exceeds the size limit
        const tooLargeFile = files.find(
          file => sizeLimit && convertBytes(file.size, 'MB') > sizeLimit
        );
        if (tooLargeFile) {
          const { size, name } = tooLargeFile;
          const sizeInMB = convertBytes(size, 'MB');
          this.upgradeModalOpen = true;
          this.createError({
            text: `Asset: ${name} is ${sizeInMB} MB and this workspace's plan has a size limit of ${sizeLimit} MB`,
          });
          return false;
        }
      }

      return true;
    },

    /**
     * Creates temporary assets before uploading to dipslay the spinner icon
     * @param   {array} files  The various files that are to be uploaded
     * @return  {Promise}      The asset ids of the created assets
     */
    async createTemporaryAssets(files) {
      // Get default container
      let tId = this.getContainerId();
      // Get default tIndex
      let tIndex = this.getContainerIndex();
      if (this.getIsRecursiveContainer(this.assetId)) {
        // Get Current item instead of container ( current item as container and tIndex 0 )
        tId = this.assetId;
        tIndex = this.addEnd ? '-' : 0;
      }
      // This method will return when all assets have been created
      const results = await Promise.allSettled(
        files.map(async file =>
          this.createAsset({
            props: { type: 'image', file: null, name: file.name },
            tId,
            tIndex,
            tExpand: true,
          })
        )
      );

      console.log('Results are:', results);
      const assetIds = results.map(r => r.value);
      console.log('AssetIds are:', assetIds);
      return Promise.resolve(assetIds);
    },

    /**
     * Upload the files in the fileList and inform the relevant asset ids
     * @param {array} files         The various files that are to be uploaded
     * @param {array} assetIds      The ids of the temporary assets that will store the data once uplaoded
     * @param {array} previousProps An array with the previous asset props to restore on failure (if does not exist delete the asset)
     */
    async uploadFiles(files, assetIds, previousProps) {
      // This method will return when all uploads have been settled
      console.log(files);
      await Promise.all(
        // We issue each call as a separate request because some may fail or finish before the others
        files.map(async (file, index) => {
          if (file.type.startsWith('audio')) {
            requests.assets
              .uploadAudioFile({
                hash: this.getOpen,
                audio: file,
              })
              .then(response => {
                const { filename, name, size } = response.data;
                const id = assetIds[index];
                const properties = {
                  file: filename,
                  size,
                  type: 'audio',
                  volume: 1,
                };
                if (!this.replace) {
                  properties.name = name;
                }
                this.updateAsset({ id, props: properties, apply: true });
              })
              .catch(error => {
                console.log('Audio uploading error', error);
                const id = assetIds[index];
                if (previousProps) {
                  const props = previousProps[index];
                  this.updateAsset({ id, props, apply: true });
                } else {
                  this.deleteAsset({ id, apply: true });
                }
                const { detail } = error.response.data;
                const { statusText } = error.response;
                if (detail) {
                  this.createError({ text: detail });
                } else if (statusText) {
                  this.createError({ text: statusText });
                }
              });
          } else if (file.type.startsWith('image')) {
            requests.assets
              .uploadImageFile({
                image: file,
                hash: this.getOpen,
                small: { width: 240, height: 240 },
                medium: { width: 500, height: 240 },
              })
              .then(response => {
                const { filename, name, size } = response.data;
                const id = assetIds[index];
                const properties = { file: filename, size, type: 'image' };
                if (!this.replace) {
                  properties.name = name;
                }
                this.updateAsset({ id, props: properties, apply: true });
              })
              .catch(error => {
                console.log('Asset uploading error', error);
                const id = assetIds[index];
                if (previousProps) {
                  const props = previousProps[index];
                  this.updateAsset({ id, props, apply: true });
                } else {
                  this.deleteAsset({ id, apply: true });
                }
                const { detail } = error.response.data;
                const { statusText } = error.response;
                if (detail) {
                  this.createError({ text: detail });
                } else if (statusText) {
                  this.createError({ text: statusText });
                }
              });
          }
        })
      );
    },

    /**
     * Returns the container id
     */
    getContainerId() {
      const container = this.getContainerNode(
        this.assetId,
        'assets',
        'children'
      );
      if (!container) return null;
      return container.id;
    },

    /**
     * Returns the container index in the container
     */
    getContainerIndex() {
      return this.getIndex(this.assetId, 'assets', 'children');
    },
  },
};
</script>

<style lang="scss" scoped>
.input-hidden {
  display: none;
}
</style>
